/**
 * AIAttachmentSummary - Sprint 11
 * AI-powered summary panel for demand attachments
 * Human-in-the-loop: Shows draft summary for review before accepting
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Target, FileOutput, AlertTriangle, Users,
  HelpCircle, CheckCircle, Loader2, RefreshCw, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAttachmentSummary = ({ submissionId, attachmentCount, onSummaryAccepted }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  };

  const generateSummary = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/intake-queue/${submissionId}/ai-summary`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSummary(data.summary);
          toast.success(`AI summary generated in ${Math.round(data.latency_ms)}ms`);
        } else {
          setError(data.error || 'Failed to generate summary');
          toast.error(data.error || 'AI service unavailable');
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to generate summary');
        toast.error(err.detail || 'Failed to generate summary');
      }
    } catch (err) {
      setError('Network error');
      toast.error('Failed to connect to AI service');
    } finally {
      setLoading(false);
    }
  };

  const acceptSummary = async () => {
    if (!summary) return;
    
    setAccepting(true);
    try {
      // Format summary text
      const summaryText = [
        summary.goal && `**Goal:** ${summary.goal}`,
        summary.expected_output && `**Expected Output:** ${summary.expected_output}`,
        summary.constraints?.length > 0 && `**Constraints:**\n${summary.constraints.map(c => `- ${c}`).join('\n')}`,
        summary.stakeholders?.length > 0 && `**Stakeholders:** ${summary.stakeholders.join(', ')}`,
        summary.open_questions?.length > 0 && `**Open Questions:**\n${summary.open_questions.map(q => `- ${q}`).join('\n')}`,
        summary.risks?.length > 0 && `**Risks:**\n${summary.risks.map(r => `- ${r}`).join('\n')}`
      ].filter(Boolean).join('\n\n');

      const response = await fetch(`${API_URL}/api/public/intake-queue/${submissionId}/ai-summary/accept`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ summary_text: summaryText })
      });

      if (response.ok) {
        toast.success('AI summary added to demand description');
        if (onSummaryAccepted) {
          onSummaryAccepted(summaryText);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to accept summary');
      }
    } catch (err) {
      toast.error('Failed to accept summary');
    } finally {
      setAccepting(false);
    }
  };

  if (attachmentCount === 0) {
    return null;
  }

  return (
    <Card className="mt-4 bg-gradient-to-br from-purple-50 to-white border-purple-200" data-testid="ai-summary-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          AI Attachment Analysis
          <Badge variant="outline" className="text-xs">Beta</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!summary && !loading && !error && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 mb-3">
              Generate an AI-powered summary of the attached documents to identify goals, 
              constraints, stakeholders, and potential risks.
            </p>
            <Button
              onClick={generateSummary}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="generate-ai-summary"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate AI Summary
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600 mr-2" />
            <span className="text-gray-600">Analyzing attachments...</span>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-4">
            <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={generateSummary}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Try Again
            </Button>
          </div>
        )}

        {summary && !loading && (
          <div className="space-y-4">
            {/* Draft badge */}
            <div className="flex items-center justify-between">
              <Badge className="bg-yellow-100 text-yellow-700">Draft - Review before accepting</Badge>
              <Button variant="ghost" size="sm" onClick={() => setSummary(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Goal */}
            {summary.goal && (
              <div className="flex gap-3">
                <Target className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Goal</p>
                  <p className="text-sm">{summary.goal}</p>
                </div>
              </div>
            )}

            {/* Expected Output */}
            {summary.expected_output && (
              <div className="flex gap-3">
                <FileOutput className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Expected Output</p>
                  <p className="text-sm">{summary.expected_output}</p>
                </div>
              </div>
            )}

            {/* Constraints */}
            {summary.constraints?.length > 0 && (
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Constraints</p>
                  <ul className="text-sm list-disc list-inside">
                    {summary.constraints.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* Stakeholders */}
            {summary.stakeholders?.length > 0 && (
              <div className="flex gap-3">
                <Users className="h-5 w-5 text-purple-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Stakeholders</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {summary.stakeholders.map((s, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Open Questions */}
            {summary.open_questions?.length > 0 && (
              <div className="flex gap-3">
                <HelpCircle className="h-5 w-5 text-cyan-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Open Questions</p>
                  <ul className="text-sm list-disc list-inside">
                    {summary.open_questions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* Risks */}
            {summary.risks?.length > 0 && (
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Risks</p>
                  <ul className="text-sm list-disc list-inside">
                    {summary.risks.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* Accept Button */}
            <div className="pt-4 border-t flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={generateSummary} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Regenerate
              </Button>
              <Button
                size="sm"
                onClick={acceptSummary}
                disabled={accepting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="accept-ai-summary"
              >
                {accepting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Accept & Add to Description
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIAttachmentSummary;
